import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../../context/ChatContext';
import Button from '../atoms/Button';
import Typography from '../atoms/Typography';
import SEO from '../atoms/SEO';
import FloatingModelsContainer from '../organisms/FloatingModelsContainer';
import './ChaptersPage.css';

const physicsChapters = [
  // Mechanics
  { 
    id: 1, 
    title: "Kinematics: Motion in One Dimension", 
    category: "Mechanics",
    topics: [
      "Position, displacement, and distance",
      "Speed and velocity",
      "Acceleration",
      "Free fall and gravity",
      "Graphs of motion"
    ]
  },
  { 
    id: 2, 
    title: "Motion in Two and Three Dimensions", 
    category: "Mechanics",
    topics: [
      "Vectors and vector operations",
      "Projectile motion",
      "Relative motion",
      "Uniform circular motion",
      "Non-uniform circular motion"
    ]
  },
  { 
    id: 3, 
    title: "Newton's Laws of Motion", 
    category: "Mechanics",
    topics: [
      "First law: inertia",
      "Second law: F = ma",
      "Third law: action-reaction",
      "Weight vs. mass",
      "Normal force and friction"
    ]
  },
  { 
    id: 4, 
    title: "Work and Energy", 
    category: "Mechanics",
    topics: [
      "Work done by constant and variable forces",
      "Kinetic energy",
      "Potential energy",
      "Conservation of energy",
      "Power and efficiency"
    ]
  },
  { 
    id: 5, 
    title: "Momentum and Collisions", 
    category: "Mechanics",
    topics: [
      "Linear momentum",
      "Impulse",
      "Conservation of momentum",
      "Elastic and inelastic collisions",
      "Center of mass"
    ]
  },
  { 
    id: 6, 
    title: "Circular Motion and Gravitation", 
    category: "Mechanics",
    topics: [
      "Uniform circular motion",
      "Centripetal acceleration",
      "Newton's law of gravitation",
      "Gravitational field and potential",
      "Kepler's laws of planetary motion"
    ]
  },
  
  // Thermodynamics
  { 
    id: 7, 
    title: "Temperature and Heat", 
    category: "Thermodynamics",
    topics: [
      "Temperature scales and thermal equilibrium",
      "Heat as energy transfer",
      "Specific heat capacity",
      "Calorimetry",
      "Phase changes and latent heat"
    ]
  },
  { 
    id: 8, 
    title: "Thermal Properties of Matter", 
    category: "Thermodynamics",
    topics: [
      "Thermal expansion",
      "Ideal gas law",
      "Kinetic theory of gases",
      "Heat transfer methods",
      "Thermal conductivity"
    ]
  },
  { 
    id: 9, 
    title: "The Laws of Thermodynamics", 
    category: "Thermodynamics",
    topics: [
      "First law: energy conservation",
      "Second law: entropy",
      "Third law: absolute zero",
      "Entropy and disorder",
      "Reversible and irreversible processes"
    ]
  },
  { 
    id: 10, 
    title: "Heat Engines and Refrigerators", 
    category: "Thermodynamics",
    topics: [
      "Heat engines and efficiency",
      "Carnot cycle",
      "Refrigerators and heat pumps",
      "Entropy change in thermal processes",
      "Applications of thermodynamics"
    ]
  },
  
  // Waves and Optics
  { 
    id: 11, 
    title: "Oscillations and Waves", 
    category: "Waves & Optics",
    topics: [
      "Simple harmonic motion",
      "Pendulums and springs",
      "Damped and forced oscillations",
      "Wave properties",
      "Wave interference and standing waves"
    ]
  },
  { 
    id: 12, 
    title: "Sound Waves", 
    category: "Waves & Optics",
    topics: [
      "Nature of sound waves",
      "Sound intensity and decibels",
      "Doppler effect",
      "Resonance and musical instruments",
      "Beats and interference"
    ]
  },
  { 
    id: 13, 
    title: "Wave Optics", 
    category: "Waves & Optics",
    topics: [
      "Light as an electromagnetic wave",
      "Interference and diffraction",
      "Young's double-slit experiment",
      "Diffraction gratings",
      "Polarization"
    ]
  },
  { 
    id: 14, 
    title: "Geometric Optics", 
    category: "Waves & Optics",
    topics: [
      "Reflection and refraction",
      "Snell's law",
      "Lenses and image formation",
      "Mirrors",
      "Optical instruments"
    ]
  },
  
  // Electricity and Magnetism
  { 
    id: 15, 
    title: "Electric Charges and Forces", 
    category: "Electromagnetism",
    topics: [
      "Electric charge and its conservation",
      "Conductors and insulators",
      "Coulomb's law",
      "Charge distribution",
      "Electric dipoles"
    ]
  },
  { 
    id: 16, 
    title: "Electric Fields", 
    category: "Electromagnetism",
    topics: [
      "Electric field concept",
      "Field lines and flux",
      "Gauss's law",
      "Fields of various charge distributions",
      "Motion of charges in electric fields"
    ]
  },
  { 
    id: 17, 
    title: "Electric Potential", 
    category: "Electromagnetism",
    topics: [
      "Electric potential energy",
      "Electric potential (voltage)",
      "Equipotential surfaces",
      "Potential gradient",
      "Capacitance and dielectrics"
    ]
  },
  { 
    id: 18, 
    title: "Electric Circuits", 
    category: "Electromagnetism",
    topics: [
      "Current and resistance",
      "Ohm's law",
      "DC circuits",
      "Kirchhoff's rules",
      "RC circuits"
    ]
  },
  { 
    id: 19, 
    title: "Magnetic Fields and Forces", 
    category: "Electromagnetism",
    topics: [
      "Magnetic fields and field lines",
      "Biot-Savart law",
      "Ampere's law",
      "Force on moving charges",
      "Force on current-carrying wires"
    ]
  },
  { 
    id: 20, 
    title: "Electromagnetic Induction", 
    category: "Electromagnetism",
    topics: [
      "Faraday's law",
      "Lenz's law",
      "Generators and motors",
      "Inductance",
      "Maxwell's equations"
    ]
  },
  
  // Modern Physics
  { 
    id: 21, 
    title: "Special Relativity", 
    category: "Modern Physics",
    topics: [
      "Einstein's postulates",
      "Time dilation",
      "Length contraction",
      "Relativistic momentum and energy",
      "Twin paradox"
    ]
  },
  { 
    id: 22, 
    title: "Quantum Physics", 
    category: "Modern Physics",
    topics: [
      "Blackbody radiation",
      "Photoelectric effect",
      "Wave-particle duality",
      "Uncertainty principle",
      "Quantum tunneling"
    ]
  },
  { 
    id: 23, 
    title: "Atomic Physics", 
    category: "Modern Physics",
    topics: [
      "Rutherford's model",
      "Bohr model",
      "Spectroscopy",
      "Quantum numbers",
      "Electron configurations"
    ]
  },
  { 
    id: 24, 
    title: "Nuclear Physics", 
    category: "Modern Physics",
    topics: [
      "Nuclear structure",
      "Binding energy",
      "Radioactivity",
      "Nuclear reactions",
      "Fission and fusion"
    ]
  },
  
  // Astronomy and Astrophysics
  { 
    id: 25, 
    title: "The Solar System", 
    category: "Astronomy",
    topics: [
      "Formation of the solar system",
      "The Sun and its properties",
      "Planets and moons",
      "Asteroids, comets, and meteoroids",
      "Exoplanets"
    ]
  },
  { 
    id: 26, 
    title: "Stars and Stellar Evolution", 
    category: "Astronomy",
    topics: [
      "Stellar properties and classification",
      "Hertzsprung-Russell diagram",
      "Stellar energy sources",
      "Star formation and evolution",
      "Supernovae, neutron stars, and black holes"
    ]
  },
  { 
    id: 27, 
    title: "Galaxies", 
    category: "Astronomy",
    topics: [
      "Types of galaxies",
      "Milky Way structure",
      "Galaxy formation and evolution",
      "Active galactic nuclei",
      "Galaxy clusters"
    ]
  },
  { 
    id: 28, 
    title: "Cosmology", 
    category: "Astronomy",
    topics: [
      "Big Bang theory",
      "Cosmic microwave background",
      "Dark matter and dark energy",
      "Expanding universe",
      "Fate of the universe"
    ]
  },
  
  // Mathematics for Physics
  { 
    id: 29, 
    title: "Calculus for Physics", 
    category: "Mathematics",
    topics: [
      "Derivatives and integrals in physics",
      "Differential equations",
      "Vector calculus",
      "Multiple integrals",
      "Series and approximations"
    ]
  },
  { 
    id: 30, 
    title: "Vectors and Coordinate Systems", 
    category: "Mathematics",
    topics: [
      "Vector operations",
      "Coordinate transformations",
      "Spherical and cylindrical coordinates",
      "Tensors",
      "Applications in mechanics and E&M"
    ]
  }
];

const ChaptersPage = () => {
  const navigate = useNavigate();
  const { startChapterTutoring } = useChat();
  const [expandedChapter, setExpandedChapter] = useState(null);
  const [visibleCategories, setVisibleCategories] = useState({});
  
  // Group chapters by category
  const chaptersByCategory = physicsChapters.reduce((acc, chapter) => {
    if (!acc[chapter.category]) {
      acc[chapter.category] = [];
    }
    acc[chapter.category].push(chapter);
    return acc;
  }, {});
  
  // Initialize all categories as visible
  useEffect(() => {
    const initialVisibleState = {};
    Object.keys(chaptersByCategory).forEach(category => {
      initialVisibleState[category] = true;
    });
    setVisibleCategories(initialVisibleState);
  }, [chaptersByCategory]);
  
  const handleChapterSelect = (chapter) => {
    startChapterTutoring(chapter.title);
    navigate('/chat');
  };
  
  const handleBackClick = () => {
    navigate('/');
  };

  const toggleChapterExpansion = (chapterId) => {
    if (expandedChapter === chapterId) {
      setExpandedChapter(null);
    } else {
      setExpandedChapter(chapterId);
    }
  };
  
  const toggleCategoryVisibility = (category) => {
    setVisibleCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  return (
    <div className="chapters-page">
      <div className="gradient-overlay"></div>
      <div className="grid-pattern"></div>
      
      <FloatingModelsContainer className="chapters-floating-models" />
      
      <SEO 
        title="Physics Chapters | Astronaut Tutor AI"
        description="Explore a comprehensive list of physics chapters to study with AI assistance."
      />
      
      <div className="chapters-header">
        <Button
          onClick={handleBackClick}
          variant="secondary"
          className="back-button"
        >
          ← Back
        </Button>
        
        <Typography variant="h1" className="chapters-title">
          Physics Chapters
        </Typography>
        
        <Typography variant="body1" className="chapters-subtitle">
          Select a chapter to start learning with your AI tutor
        </Typography>
      </div>
      
      <div className="chapters-container">
        {Object.entries(chaptersByCategory).map(([category, chapters]) => (
          <div key={category} className="category-section">
            <Typography 
              variant="h2" 
              className="category-title"
              onClick={() => toggleCategoryVisibility(category)}
            >
              {category}
              <span className="expand-icon" style={{ marginLeft: '10px' }}>
                {visibleCategories[category] ? '−' : '+'}
              </span>
            </Typography>
            
            {visibleCategories[category] && (
              <div className="chapters-grid">
                {chapters.map(chapter => (
                  <div 
                    key={chapter.id} 
                    className={`chapter-card ${expandedChapter === chapter.id ? 'expanded' : ''}`}
                  >
                    <Button 
                      onClick={() => toggleChapterExpansion(chapter.id)}
                      variant="secondary"
                      className="chapter-button"
                    >
                      <span className="chapter-number">{chapter.id}</span>
                      <span className="chapter-title">
                        {chapter.title}
                        <span className="expand-icon">{expandedChapter === chapter.id ? '−' : '+'}</span>
                      </span>
                    </Button>
                    
                    {expandedChapter === chapter.id && (
                      <div className="chapter-topics">
                        <h4>
                          Topics:
                          <Button 
                            onClick={() => handleChapterSelect(chapter)}
                            variant="primary"
                            className="start-learning-btn"
                          >
                            Start Learning
                          </Button>
                        </h4>
                        <ul>
                          {chapter.topics.map((topic, index) => (
                            <li key={index}>{topic}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChaptersPage; 